import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AppointmentFormats, formatDateTime, messages } from '@nesc/shared';
import { api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input, Select, Textarea } from '@/components/ui/input';

/** アポイントの登録。本文から検出した日時を初期値にする */
export const AppointmentDialog = ({
  open,
  onOpenChange,
  companyId,
  inboundMessageId,
  detectedDates = [],
  appointmentId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string | null;
  inboundMessageId?: string;
  detectedDates?: string[];
  appointmentId?: string;
}) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    scheduledAt: '',
    durationMinutes: 60,
    format: 'online' as 'visit' | 'online' | 'phone',
    location: '',
    ownerId: '',
    purpose: '',
    note: '',
  });

  // 検出した日時があれば初期値に入れる
  useEffect(() => {
    if (open && detectedDates.length > 0 && !form.scheduledAt) {
      const first = new Date(detectedDates[0]);
      if (!Number.isNaN(first.getTime())) {
        setForm((prev) => ({ ...prev, scheduledAt: toLocalInput(first) }));
      }
    }
  }, [open, detectedDates, form.scheduledAt]);

  const { data: users } = useQuery({
    queryKey: qk.assignableUsers,
    queryFn: () =>
      api.get<SingleResponse<{ id: string; name: string | null; email: string }[]>>('/users/assignable'),
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: () =>
      appointmentId
        ? api.patch(`/appointments/${appointmentId}`, {
            scheduledAt: new Date(form.scheduledAt).toISOString(),
            durationMinutes: form.durationMinutes,
            format: form.format,
            location: form.location || undefined,
            ownerId: form.ownerId || undefined,
            purpose: form.purpose || undefined,
            note: form.note || undefined,
          })
        : api.post('/appointments', {
            companyId,
            inboundMessageId,
            scheduledAt: new Date(form.scheduledAt).toISOString(),
            durationMinutes: form.durationMinutes,
            format: form.format,
            location: form.location || undefined,
            ownerId: form.ownerId || undefined,
            purpose: form.purpose || undefined,
            note: form.note || undefined,
            updateCompanyStatus: true,
          }),
    onSuccess: () => {
      toast.success(appointmentId ? 'アポイントを変更しました' : 'アポイントを登録しました');
      void queryClient.invalidateQueries({ queryKey: ['appointments'] });
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
      if (inboundMessageId) void queryClient.invalidateQueries({ queryKey: qk.inboxItem(inboundMessageId) });
      onOpenChange(false);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const locationRequired = form.format === 'visit' || form.format === 'online';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {appointmentId ? messages.appointment.update : messages.appointment.create}
          </DialogTitle>
          <DialogDescription>{messages.appointment.confirmStatusUpdate}</DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {detectedDates.length > 0 && (
            <div className="rounded-control border border-border bg-surface p-3">
              <p className="mb-1.5 text-note font-semibold text-foreground-muted">
                本文から検出した日時
              </p>
              <div className="flex flex-wrap gap-2">
                {detectedDates.map((date) => (
                  <Button
                    key={date}
                    variant="outline"
                    size="sm"
                    onClick={() => setForm((prev) => ({ ...prev, scheduledAt: toLocalInput(new Date(date)) }))}
                  >
                    {formatDateTime(date)}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <Field label={messages.appointment.scheduledAt} required>
            {(props) => (
              <Input
                {...props}
                type="datetime-local"
                value={form.scheduledAt}
                onChange={(e) => setForm((p) => ({ ...p, scheduledAt: e.target.value }))}
              />
            )}
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={`${messages.appointment.duration}（分）`} required>
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={5}
                  max={600}
                  value={form.durationMinutes}
                  onChange={(e) => setForm((p) => ({ ...p, durationMinutes: Number(e.target.value) }))}
                />
              )}
            </Field>
            <Field label={messages.appointment.format} required>
              {(props) => (
                <Select
                  {...props}
                  value={form.format}
                  onChange={(e) => setForm((p) => ({ ...p, format: e.target.value as never }))}
                >
                  {AppointmentFormats.list.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>

          {locationRequired && (
            <Field
              label={form.format === 'visit' ? messages.appointment.location : messages.appointment.locationUrl}
              required
            >
              {(props) => (
                <Input
                  {...props}
                  value={form.location}
                  onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))}
                  placeholder={form.format === 'visit' ? '東京都渋谷区…' : 'https://meet.example.com/…'}
                />
              )}
            </Field>
          )}

          <Field label={messages.appointment.owner}>
            {(props) => (
              <Select
                {...props}
                value={form.ownerId}
                onChange={(e) => setForm((p) => ({ ...p, ownerId: e.target.value }))}
              >
                <option value="">自分</option>
                {users?.data.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name ?? user.email}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label={messages.appointment.purpose}>
            {(props) => (
              <Input {...props} value={form.purpose} onChange={(e) => setForm((p) => ({ ...p, purpose: e.target.value }))} />
            )}
          </Field>

          <Field label={messages.appointment.note}>
            {(props) => (
              <Textarea {...props} rows={3} value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} />
            )}
          </Field>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" size="lg" onClick={() => onOpenChange(false)}>
            {messages.common.cancel}
          </Button>
          <Button
            size="lg"
            onClick={() => mutation.mutate()}
            disabled={
              mutation.isPending || !form.scheduledAt || (locationRequired && !form.location.trim())
            }
          >
            {messages.common.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** datetime-local に渡すローカル時刻文字列 */
const toLocalInput = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
